import { Feather } from '@expo/vector-icons'
import { Input as NBInput, Pressable, Box } from 'native-base'
import { forwardRef } from 'react'
import {
  TextInput,
  InputAccessoryView,
  Button,
  NativeSyntheticEvent,
  TextInputFocusEventData,
} from 'react-native'

import type { InputProps } from '.'

import {
  useSecurePassword,
  useToggle,
  useDeviceInfo,
  useTheme,
  useTranslation,
  useCallback,
} from '~hooks'
import { hex2rgba } from '~utils'

/**
 * Extended native-base Input component with a secure password icon.
 */

export const Input = forwardRef<TextInput, InputProps>(
  (
    {
      editable = true,
      inputAccessoryViewLabel,
      isError = false,
      name,
      onCustomInputAccessoryPress,
      secureTextIconColor,
      secureTextIconName,
      secureTextIconSize = 24,
      ...props
    },
    ref
  ) => {
    const { isIOS } = useDeviceInfo()
    const { colors } = useTheme()
    const { t } = useTranslation()
    const { securePassword, toggleSecurePassword, iconName } = useSecurePassword(props.type)
    const [isFocused, setIsFocused] = useToggle(false)
    const handleFocus = useCallback(
      (e: NativeSyntheticEvent<TextInputFocusEventData>) => {
        props?.onFocus?.(e)
        setIsFocused()
      },
      [props, setIsFocused]
    )
    return (
      <>
        <NBInput
          {...props}
          autoCapitalize="none"
          bgColor={isFocused ? hex2rgba(colors.primary['400'], 0.05) : 'white'}
          borderColor={isError ? 'error.600' : !editable ? 'muted.200' : isFocused ? 'actionBlue' : 'muted.200'}
          borderWidth={isFocused ? 2 : 1}
          borderRadius="xl"
          minH="12"
          px="4"
          py="3"
          fontSize="md"
          fontWeight="500"
          color="gray.800"
          placeholderTextColor="gray.400"
          {...(!editable && { color: 'muted.300' })}
          editable={editable}
          focusOutlineColor="transparent"
          _hover={{
            borderColor: isError ? 'error.600' : 'actionBlue',
            bgColor: hex2rgba(colors.primary['400'], 0.02),
          }}
          _focus={{
            borderColor: 'actionBlue',
            bgColor: hex2rgba(colors.primary['400'], 0.05),
            borderWidth: 2,
          }}
          _disabled={{
            bgColor: 'gray.50',
            borderColor: 'gray.200',
            color: 'gray.400',
          }}
          onBlur={setIsFocused}
          onFocus={handleFocus}
          ref={ref || undefined}
          rightElement={
            props.type === 'password' ? (
              <Pressable mr={3} onPress={toggleSecurePassword}>
                <Feather
                  name={secureTextIconName || iconName}
                  color={secureTextIconColor || (isFocused ? 'actionBlue' : 'gray.400')}
                  size={secureTextIconSize}
                />
              </Pressable>
            ) : (
              props.rightElement
            )
          }
          secureTextEntry={securePassword}
          size={props.size || 'xl'}
          value={props.value}
          {...(isIOS && name && { inputAccessoryViewID: `input-accessory-${name}` })}
        />
        {onCustomInputAccessoryPress && isIOS && name ? (
          <InputAccessoryView backgroundColor={colors.white} nativeID={`input-accessory-${name}`}>
            <Box alignItems="flex-end" pr="5" bgColor={'activeBlue'} w="full">
              <Button
                title={inputAccessoryViewLabel || t('common.next')}
                onPress={onCustomInputAccessoryPress}
              />
            </Box>
          </InputAccessoryView>
        ) : null}
      </>
    )
  }
)
